/**
 * UI 总控管理器
 * 连接游戏引擎与各 UI 面板
 */

import { GameEngine } from '../engine/GameEngine';
import type { GameEvent } from '../engine/GameEngine';
import { StatsPanel } from './StatsPanel';
import { ActionPanel } from './ActionPanel';
import type { ActionType } from './ActionPanel';
import { CombatPanel } from './CombatPanel';
import { LogPanel } from './LogPanel';
import { EquipmentPanel } from './EquipmentPanel';
import type { EquipActionPayload } from './EquipmentPanel';
import { ShopPanel } from './ShopPanel';
import type { ShopActionPayload } from './ShopPanel';
import { PouchPanel } from './PouchPanel';
import type { PouchActionPayload } from './PouchPanel';
import { BreathingGame } from './BreathingGame';
import { TechniquePanel } from './TechniquePanel';
import type { TechniqueActionPayload } from './TechniquePanel';
import { AlchemyPanel } from './AlchemyPanel';
import type { AlchemyActionPayload } from './AlchemyPanel';
import { ForgePanel } from './ForgePanel';
import type { ForgeActionPayload } from './ForgePanel';
import { AchievementPanel } from './AchievementPanel';
import { SectPanel } from './SectPanel';
import type { SectActionPayload } from './SectPanel';
import { ZonePanel } from './ZonePanel';
import type { ZoneActionPayload } from './ZonePanel';
import { MeridianPanel } from './MeridianPanel';
import type { MeridianActionPayload } from './MeridianPanel';
import { ReincarnationPanel } from './ReincarnationPanel';
import { isPlayerDead, getPlayerOrigin, getPlayerIdentity } from '../engine/Player';
import { getRootDisplayName, getRootDescription } from '../data/spiritualRoots';
import { getSaveTime } from '../engine/SaveManager';
import type { GameEvent as RandomEvent, EventChoice } from '../data/events';

export class UIManager {
  private engine: GameEngine;
  private statsPanel: StatsPanel;
  private equipmentPanel: EquipmentPanel;
  private pouchPanel: PouchPanel;
  private actionPanel: ActionPanel;
  private combatPanel: CombatPanel;
  private logPanel: LogPanel;
  private shopPanel: ShopPanel;
  private breathingGame: BreathingGame;
  private techniquePanel: TechniquePanel;
  private alchemyPanel: AlchemyPanel;
  private forgePanel: ForgePanel;
  private achievementPanel: AchievementPanel;
  private sectPanel: SectPanel;
  private zonePanel: ZonePanel;
  private meridianPanel: MeridianPanel;
  private unsubEngine: (() => void) | null = null;
  /** 离线时长描述（在引擎处理离线收益前保存） */
  private idleMessage: string | null = null;

  constructor() {
    // 在引擎处理离线收益前，先捕获离线时间描述
    this.captureIdleMessage();

    this.engine = new GameEngine();

    this.statsPanel = new StatsPanel('stats-panel');
    this.equipmentPanel = new EquipmentPanel('equipment-panel');
    this.pouchPanel = new PouchPanel('pouch-panel');
    this.actionPanel = new ActionPanel('action-panel');
    this.combatPanel = new CombatPanel('combat-panel');
    this.logPanel = new LogPanel('log-panel');
    this.shopPanel = new ShopPanel();
    this.breathingGame = new BreathingGame();
    this.techniquePanel = new TechniquePanel();
    this.alchemyPanel = new AlchemyPanel();
    this.forgePanel = new ForgePanel();
    this.achievementPanel = new AchievementPanel();
    this.sectPanel = new SectPanel();
    this.zonePanel = new ZonePanel();
    this.meridianPanel = new MeridianPanel();
  }

  /** 启动 UI */
  start(): void {
    // 引擎事件 → UI 更新
    this.unsubEngine = this.engine.on((event) => this.handleEngineEvent(event));

    // 操作按钮 → 引擎
    this.actionPanel.setOnAction((action) => this.handleAction(action));

    // 装备面板操作
    this.equipmentPanel.setOnAction((payload) => this.handleEquipmentAction(payload));

    // 背包操作
    this.pouchPanel.setOnAction((payload) => this.handlePouchAction(payload));

    // 功法面板操作
    this.techniquePanel.setOnAction((payload) => this.handleTechniqueAction(payload));

    // 炼丹面板操作
    this.alchemyPanel.setOnAction((payload) => this.handleAlchemyAction(payload));

    // 炼器面板操作
    this.forgePanel.setOnAction((payload) => this.handleForgeAction(payload));

    // 宗门面板操作
    this.sectPanel.setOnAction((payload) => this.handleSectAction(payload));

    // 区域探索操作
    this.zonePanel.setOnAction((payload) => this.handleZoneAction(payload));

    // 经脉操作
    this.meridianPanel.setOnAction((payload) => this.handleMeridianAction(payload));

    // 商店操作
    this.shopPanel.setOnAction((payload) => this.handleShopAction(payload));

    // 初始渲染
    this.refreshUI();

    // 启动游戏循环
    this.engine.start();

    // 新玩家 — 投胎转世体验
    if (this.engine.isNewPlayer) {
      const panel = new ReincarnationPanel();
      panel.start(this.engine.player, (name) => {
        this.engine.player.name = name;
        this.engine.doSave();
        // 投胎结束后显示开场日志
        this.refreshUI();
        this.showOpeningLog();
      });
      // 开场日志延迟到投胎结束后
      return;
    }

    this.showOpeningLog();

    this.showOpeningLog();
  }

  private showOpeningLog(): void {
    this.logPanel.add('🌟 命运的齿轮开始转动...');

    const rootName = getRootDisplayName(this.engine.player.spiritualRoot);
    const rootDesc = getRootDescription(this.engine.player.spiritualRoot);
    this.logPanel.add(`🔮 灵根觉醒：${rootName}`);
    this.logPanel.add(`   ${rootDesc}`);

    const origin = getPlayerOrigin(this.engine.player);
    this.logPanel.add(`🏠 出身：${origin.name} — ${origin.desc}`);

    const identity = getPlayerIdentity(this.engine.player);
    this.logPanel.add(`🎯 身份：${identity.name} — ${identity.desc}`);
    if (identity.id === 'imprisoned') {
      this.logPanel.add('⛓️ 你被邪修囚禁。血月之夜将至——你必须在那之前逃出生天！');
    } else if (identity.id === 'expelled') {
      this.logPanel.add('💪 "灵根差又如何？韩立能做到的，我也能。"');
    }

    const realmName = this.engine.player.realmKey.replace(/_/g, ' ').replace(/(\d+)$/, '第$1层');
    this.logPanel.add(`道号：${this.engine.player.name} | 境界：${realmName}`);

    if (this.idleMessage) {
      this.showIdleNotification(this.idleMessage);
    }
  }

  /** 停止 UI */
  stop(): void {
    this.engine.stop();
    this.shopPanel.close();
    this.techniquePanel.close();
    this.alchemyPanel.close();
    this.forgePanel.close();
    this.sectPanel.close();
    this.meridianPanel.close();
    if (this.unsubEngine) {
      this.unsubEngine();
      this.unsubEngine = null;
    }
  }

  /** 处理引擎事件 */
  private handleEngineEvent(event: GameEvent): void {
    // 记录日志
    if (event.message) {
      this.logPanel.add(event.message);
    }

    // 按事件类型处理
    switch (event.type) {
      case 'tick':
        this.refreshUI();
        break;

      case 'combat_start':
        this.actionPanel.setInCombat(true);
        if (event.data) {
          this.combatPanel.showEnemy(event.data);
        }
        break;

      case 'combat_round':
        if (event.data) {
          this.combatPanel.showEnemy(event.data);
        }
        break;

      case 'combat_end':
        this.actionPanel.setInCombat(false);
        this.combatPanel.showEmpty();
        this.refreshUI();
        break;

      case 'player_died':
        this.showDeathOverlay(event.message);
        break;

      case 'player_revived':
        this.actionPanel.setIsDead(false);
        this.refreshUI();
        break;

      case 'save':
      case 'load':
      case 'delete_save':
        this.refreshUI();
        break;

      case 'breakthrough':
        if (event.data?.breakthrough) {
          this.showBreakthroughEffect();
        }
        this.refreshUI();
        break;

      case 'equip':
      case 'unequip':
      case 'buy_item':
      case 'use_item':
      case 'discard_item':
        this.refreshUI();
        // 购买后刷新商店价格
        if (event.type === 'buy_item' && this.shopPanel.isOpen()) {
          this.shopPanel.open(this.engine.player);
        }
        break;

      case 'idle_reward':
        this.refreshUI();
        break;

      case 'event_trigger':
        if (event.data) {
          this.showEventModal(event.data);
        }
        break;

      case 'event_resolved':
        this.refreshUI();
        break;

      case 'achievement':
        if (event.data) AchievementPanel.showUnlockToast(event.data);
        this.refreshUI();
        break;

      case 'alchemy':
      case 'forge':
      case 'meridian':
      case 'sect':
      case 'learn_technique':
      case 'upgrade_technique':
      case 'active_cultivate':
        this.refreshUI();
        break;

      default:
        this.refreshUI();
        break;
    }
  }

  /** 处理用户操作 */
  private handleAction(action: ActionType): void {
    switch (action) {
      case 'cultivate':
        this.engine.doCultivate();
        break;
      case 'breakthrough':
        this.engine.doBreakthrough();
        break;
      case 'combat_zone':
        this.zonePanel.open(this.engine.player);
        break;
      case 'combat_attack':
        this.engine.doCombatAttack();
        break;
      case 'combat_flee':
        this.engine.doCombatFlee();
        break;
      case 'combat_item':
        this.showCombatItemSelect();
        break;
      case 'technique':
        this.techniquePanel.open(this.engine.player);
        break;
      case 'alchemy':
        this.alchemyPanel.open(this.engine.player);
        break;
      case 'forge':
        this.forgePanel.open(this.engine.player);
        break;
      case 'achievement':
        this.achievementPanel.open(this.engine.player);
        break;
      case 'sect':
        this.sectPanel.open(this.engine.player);
        break;
      case 'active_cultivate':
        this.breathingGame.start((result) => {
          this.engine.doActiveCultivate(result.efficiency);
        });
        break;
      case 'explore_vein':
        if (this.engine.player.territory) {
          this.engine.doLeaveVein();
        } else {
          this.engine.doExploreVein();
        }
        break;
      case 'meridian':
        this.meridianPanel.open(this.engine.player);
        break;
      case 'shop':
        this.shopPanel.open(this.engine.player);
        break;
      case 'delete_save':
        if (confirm('确定要删除存档重新投胎吗？')) {
          this.engine.doDeleteSave();
          location.reload();
        }
        break;
    }
  }

  /** 处理装备操作 */
  private handleEquipmentAction(payload: EquipActionPayload): void {
    switch (payload.action) {
      case 'equip':
        if (payload.itemId) this.engine.doEquip(payload.itemId);
        break;
      case 'unequip':
        if (payload.slot) this.engine.doUnequip(payload.slot);
        break;
      case 'enhance':
        if (payload.slot) this.engine.doEnhance(payload.slot);
        break;
    }
  }

  /** 处理背包操作 */
  private handlePouchAction(payload: PouchActionPayload): void {
    switch (payload.action) {
      case 'use':
        this.engine.doUseItem(payload.itemId);
        break;
      case 'discard':
        if (confirm('确定要丢弃这个物品吗？')) {
          this.engine.doDiscardItem(payload.itemId);
        }
        break;
    }
  }

  /** 处理经脉操作 */
  private handleMeridianAction(payload: MeridianActionPayload): void {
    if (payload.action === 'open' && payload.meridianId) {
      this.engine.doOpenMeridian(payload.meridianId);
      this.meridianPanel.open(this.engine.player);
    }
  }

  /** 处理区域探索操作 */
  private handleZoneAction(payload: ZoneActionPayload): void {
    if (payload.action === 'enter' && payload.zone) {
      this.engine.doCombat(payload.zone);
      this.zonePanel.close();
    }
  }

  /** 处理宗门操作 */
  private handleSectAction(payload: SectActionPayload): void {
    switch (payload.action) {
      case 'submit': this.engine.doSectSubmit(); break;
      case 'refresh': this.engine.doSectRefresh(); break;
      case 'redeem': if (payload.shopItemId) this.engine.doSectRedeem(payload.shopItemId); break;
    }
    if (payload.action !== 'close') this.sectPanel.open(this.engine.player);
  }

  /** 处理炼器操作 */
  private handleForgeAction(payload: ForgeActionPayload): void {
    if (payload.action === 'close') { this.forgePanel.close(); return; }
    if (payload.action === 'craft' && payload.recipeId) this.engine.doForge(payload.recipeId);
    this.forgePanel.open(this.engine.player);
  }

  /** 处理炼丹操作 */
  private handleAlchemyAction(payload: AlchemyActionPayload): void {
    switch (payload.action) {
      case 'craft':
        if (payload.recipeId) this.engine.doAlchemy(payload.recipeId);
        this.alchemyPanel.open(this.engine.player); // 刷新面板
        break;
      case 'complete':
        if (payload.recipeId && payload.extraItemId) this.engine.doCompleteRecipe(payload.recipeId, payload.extraItemId);
        this.alchemyPanel.open(this.engine.player);
        break;
      case 'close':
        this.alchemyPanel.close();
        break;
    }
  }

  /** 处理功法操作 */
  private handleTechniqueAction(payload: TechniqueActionPayload): void {
    switch (payload.action) {
      case 'upgrade':
        this.engine.doUpgradeTechnique();
        this.techniquePanel.open(this.engine.player); // 刷新面板
        break;
      case 'learn':
        if (payload.techId) this.engine.doLearnTechnique(payload.techId);
        this.techniquePanel.open(this.engine.player);
        break;
      case 'close':
        this.techniquePanel.close();
        break;
    }
  }

  /** 处理商店操作 */
  private handleShopAction(payload: ShopActionPayload): void {
    switch (payload.action) {
      case 'buy':
        if (payload.itemId) this.engine.doBuyItem(payload.itemId, payload.quantity ?? 1);
        break;
      case 'sell':
        if (payload.itemId) this.engine.doSellItem(payload.itemId, payload.quantity ?? 1);
        break;
      case 'close':
        this.shopPanel.close();
        break;
    }
  }

  /** 刷新所有 UI（增量更新） */
  private refreshUI(): void {
    this.statsPanel.update(this.engine.player);
    this.equipmentPanel.render(this.engine.player);
    this.pouchPanel.render(this.engine.player);
    this.actionPanel.setIsDead(isPlayerDead(this.engine.player));
    this.actionPanel.setInCombat(this.engine.combatState !== null && !this.engine.combatState.isOver);
  }

  /** 在引擎处理前捕获离线时间描述 */
  private captureIdleMessage(): void {
    const saveTime = getSaveTime();
    if (!saveTime) return;

    const elapsedMs = Date.now() - saveTime;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);

    if (elapsedSeconds < 30) return;

    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);

    let timeStr = '';
    if (hours > 0) timeStr += `${hours}小时`;
    if (minutes > 0) timeStr += `${minutes}分钟`;
    if (!timeStr) timeStr = `${elapsedSeconds}秒`;

    this.idleMessage = `⏰ 离线 ${timeStr}，你的角色在持续修炼中...`;
  }

  /** 显示离线收益通知 */
  private showIdleNotification(message: string): void {
    const main = document.getElementById('game-main');
    if (!main) return;

    const notif = document.createElement('div');
    notif.className = 'idle-notification';
    notif.textContent = message;
    main.parentNode?.insertBefore(notif, main);

    // 5秒后移除
    setTimeout(() => {
      notif.style.opacity = '0';
      notif.style.transition = 'opacity 1s';
      setTimeout(() => notif.remove(), 1000);
    }, 5000);
  }

  /** 显示奇遇事件弹窗 */
  private showEventModal(event: RandomEvent): void {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const choicesHtml = event.choices.map((c, i) =>
      `<button class="btn btn-primary event-choice" data-idx="${i}">${c.text}</button>`
    ).join('');

    overlay.innerHTML = `
      <div class="modal event-modal">
        <h2>✨ ${escapeHtml2(event.title)}</h2>
        <p class="modal-text">${escapeHtml2(event.desc)}</p>
        <div class="modal-buttons" style="flex-direction:column;gap:8px;">
          ${choicesHtml}
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // 绑定选择
    overlay.querySelectorAll('.event-choice').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt((btn as HTMLElement).dataset.idx || '0');
        const choice = event.choices[idx];
        if (choice) {
          this.logPanel.add(`✨ ${event.title}：${choice.text}`);
          this.engine.resolveEvent(choice);
        }
        overlay.remove();
      });
    });

    // 单选项自动选
    if (event.choices.length === 1) {
      setTimeout(() => {
        this.engine.resolveEvent(event.choices[0]);
        overlay.remove();
      }, 1500);
    }
  }

  /** 战斗中快速使用丹药 */
  private showCombatItemSelect(): void {
    const pills = this.engine.player.inventory.filter(
      (i) => i.type === 'consumable' && (i.hpRestore || i.mpRestore)
    );

    if (pills.length === 0) {
      this.logPanel.add('背包中没有可用的丹药');
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const itemsHtml = pills.map((p) =>
      `<button class="btn combat-pill-btn" data-id="${p.id}">
        ${escapeHtml2(p.name)} — ${p.description || ''}
      </button>`
    ).join('');

    overlay.innerHTML = `
      <div class="modal" style="max-width:340px;">
        <h3>🧪 使用丹药</h3>
        <div style="display:flex;flex-direction:column;gap:6px;margin:12px 0;">
          ${itemsHtml}
        </div>
        <button class="btn btn-small combat-pill-cancel">取消</button>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelectorAll('.combat-pill-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.id!;
        this.engine.doCombatUseItem(id);
        overlay.remove();
      });
    });

    overlay.querySelector('.combat-pill-cancel')?.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
  }

  /** 显示死亡遮罩 */
  private showDeathOverlay(message: string): void {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.85);z-index:3000;display:flex;flex-direction:column;align-items:center;justify-content:center;animation:fadeIn 0.5s;';
    overlay.innerHTML = `
      <div style="font-size:4em;">💀</div>
      <div style="color:var(--danger);font-size:1.5em;font-weight:bold;margin:16px 0;">陨落</div>
      <div style="color:var(--text-secondary);text-align:center;max-width:400px;">${message}</div>
      <div style="color:var(--text-secondary);margin-top:24px;font-size:0.85em;">3秒后轮回重生...</div>
    `;
    document.body.appendChild(overlay);
  }

  /** 显示突破特效 */
  private showBreakthroughEffect(): void {
    const header = document.getElementById('game-header');
    if (!header) return;

    header.classList.add('breakthrough-effect');
    setTimeout(() => header.classList.remove('breakthrough-effect'), 1500);
  }
}

function escapeHtml2(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
